/**
 * RCS sold by Connect, as the API describes it (`app.modules.messaging.rcs`):
 * a price per billing event, the platform's card and its minimums, and each
 * client's prepaid wallet.
 */

export const RATE_KEYS = [
  'basic_message',
  'otp',
  'transactional',
  'promotional',
  'a2p_conversation',
  'p2a_conversation',
  'p2a_message',
] as const;

export type RateKey = (typeof RATE_KEYS)[number];
export type RcsRates = Record<RateKey, string>;

export interface RcsPlatformPricing {
  currency: 'INR';
  rates: RcsRates;
  minimums: RcsRates;
  minimum_balance: string;
}

export interface RcsWalletEntry {
  public_id: string;
  kind: 'top_up' | 'charge' | 'refund' | 'adjustment';
  amount: string;
  balance_after: string;
  description: string;
  usage: { event?: string; rate?: string };
  at: string;
}

export interface RcsWallet {
  public_id: string;
  balance: string;
  currency: string;
  /** What it is charged per event now: its own card or the platform's, never under the minimums. */
  rates: RcsRates;
  rate_labels: { key: RateKey; label: string }[];
  custom_pricing: boolean;
  low_balance_threshold: string | null;
  low: boolean;
  spent_30_days: string;
  entries: RcsWalletEntry[];
  has_more: boolean;
}

export interface RcsClient {
  public_id: string;
  name: string;
  own_pricing: RcsRates | null;
  minimums: RcsRates;
  wallet: RcsWallet;
}

export interface RcsOverview {
  pricing: RcsPlatformPricing;
  rate_labels: { key: RateKey; label: string }[];
}

export const rcsKeys = {
  overview: ['rcs', 'overview'] as const,
  client: (id: string) => ['rcs', 'client', id] as const,
};

/** A price as typed: rupees, up to four decimals. */
export const PRICE = /^\d{1,3}(\.\d{1,4})?$/;

/** The keys whose price is under its minimum, compared as decimals. */
export function belowMinimum(rates: RcsRates, minimums: RcsRates): RateKey[] {
  return RATE_KEYS.filter((key) => PRICE.test(rates[key]) && Number(rates[key]) < Number(minimums[key]));
}
